import { cva, type VariantProps } from "class-variance-authority";
import { type ButtonHTMLAttributes, forwardRef } from "react";

import { cn } from "@krea/shared/utils";

import { Spinner } from "./Spinner";

const buttonSize = {
  default: "default",
  sm: "sm",
  lg: "lg",
  xl: "xl",
  icon: "icon",
} as const;

const buttonVariants = cva(
  [
    "tw-inline-flex tw-shrink-0 tw-items-center tw-justify-center tw-gap-2 tw-rounded tw-outline-none tw-transition-all",
    "tw-whitespace-nowrap tw-font-semibold tw-leading-[1.3]",
    "aria-invalid:tw-border-red-500 focus-visible:tw-border-primary focus-visible:tw-ring focus-visible:tw-ring-primary/50 ",
    "disabled:tw-cursor-not-allowed disabled:tw-opacity-50",
    "[&_svg:not([class*='tw-size-'])]:tw-size-4 [&_svg]:tw-pointer-events-none [&_svg]:tw-shrink-0", // icon styles
  ],
  {
    variants: {
      variant: {
        default: [
          "tw-border tw-border-primary tw-bg-primary tw-text-white hover:tw-bg-primary/80",
        ],
        danger: [
          "tw-border tw-border-red-500 tw-bg-red-500 tw-text-white hover:tw-bg-red-500/90 focus-visible:tw-ring-red-500/20",
        ],
        success: [
          "tw-bg-mint-500 tw-text-white hover:tw-bg-mint-500/90 focus-visible:tw-ring-mint-500/20",
        ],
        secondary: ["tw-border tw-border-primary tw-bg-white tw-text-primary hover:tw-bg-primary hover:tw-text-white",],
        ghost: ["hover:tw-bg-primary-100 tw-text-primary"],
        link: [
          "tw-font-semibold tw-text-primary tw-underline-offset-4 hover:tw-underline",
        ],
      },
      size: {
        [buttonSize.default]:
          "tw-h-10 tw-px-4 tw-py-2 tw-text-base has-[>svg]:tw-px-3",
        [buttonSize.sm]:
          "tw-h-8 tw-gap-1.5 tw-rounded-md tw-px-3 tw-text-sm has-[>svg]:tw-px-2.5",
        [buttonSize.lg]:
          "tw-h-12 tw-rounded-md tw-px-6 tw-text-lg has-[>svg]:tw-px-4",
        [buttonSize.xl]:
          "tw-h-16 tw-rounded-md tw-px-8 tw-text-xl has-[>svg]:tw-px-6",
        [buttonSize.icon]: "tw-size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: buttonSize.default,
    },
    compoundVariants: [
      {
        size: buttonSize.icon,
        variant: "ghost",
        class: "tw-size-6",
      },
    ],
  },
);

const spinnerVariants = cva("tw-size-4 tw-animate-spin tw-text-white", {
  variants: {
    variant: {
      default: "tw-text-white",
      secondary: "tw-text-primary",
      danger: "tw-text-red-100",
      success: "tw-text-mint-100",
      ghost: "tw-text-primary-100",
      link: "tw-text-primary",
    },
    size: {
      [buttonSize.default]: "tw-size-4",
      [buttonSize.sm]: "tw-size-3",
      [buttonSize.lg]: "tw-size-5",
      [buttonSize.xl]: "tw-size-6",
      [buttonSize.icon]: "",
    },
  },
  defaultVariants: {
    variant: "default",
    size: buttonSize.default,
  },
});

type INativeButtonProps = ButtonHTMLAttributes<HTMLButtonElement>;

type IButtonVariantProps = VariantProps<typeof buttonVariants>;

interface IButtonWithLoadingProps
  extends INativeButtonProps,
    IButtonVariantProps {
  size: Exclude<
    (typeof buttonSize)[keyof typeof buttonSize],
    typeof buttonSize.icon
  >;
  loading?: true;
}

interface IButtonWithoutLoadingProps
  extends INativeButtonProps,
    IButtonVariantProps {
  loading?: false;
}

type IButtonProps = IButtonWithLoadingProps | IButtonWithoutLoadingProps;

const Button = forwardRef<HTMLButtonElement, IButtonProps>(
  (
    {
      className,
      variant,

      size,
      children,
      loading = false,
      disabled,
      type = "button",
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={Boolean(disabled) || loading}
        {...props}
      >
        {loading && <Spinner className={spinnerVariants({ variant, size })} />}
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";

export { Button, buttonVariants, type IButtonProps };
